<template>
  <section
    ref="section"
    class="grid-module-component">
    <div class="inner-container top">
      <div
        class="horizontal-line-top"
        :class="topLineH"
        :style="`background-color: #382C75;`"></div>
      <div
        class="vertical-line-top"
        :class="topLineV"
        :style="`background-color: #382C75;`"></div>
      <div
        class="orb"
        :class="orb"
        :style="`background-color: #382C75; box-shadow: 0 0 20px #382C75;`"></div>
    </div>
    <div class="title inner-container">
      <h2
        class="title-inner"
        v-html="data.title" />
    </div>
    <ul class="grid">
      <li
        v-for="(box, idx) in data.boxes"
        :key="`box-${idx}`"
        class="box"
        :class="{ clear: checkers(idx) }"
        :style="{
          backgroundPosition: checkers(idx)
            ? `${randBgPos()}% ${randBgPos()}%`
            : null,
        }"
        v-html="box.text"></li>
    </ul>
  </section>
</template>

<script setup>
/* #META
 {
  "NAME": "GridComponent",
  "CLASSNAME": "grid-module-component",
  "URL": "http://lifemine.local/templates/"
 }
#ENDMETA */

import useUtils from '@/composables/useUtils'
import { onMounted, onBeforeUnmount, ref } from 'vue'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

defineProps(['data'])
const { device, genUUID } = useUtils()
const topLineH = `top-line-h-${genUUID()}`
const topLineV = `top-line-v-${genUUID()}`
const section = ref()
const orb = `orb-${genUUID()}`

const checkers = (idx) => {
  if (device.value === 'desktop') {
    // Determine the row and column based on idx
    const row = Math.floor(idx / 4)
    const col = idx % 4

    // If the row is even, the clear boxes are at the even columns
    // If the row is odd, the clear boxes are at the odd columns
    return (row % 2 === 0 && col % 2 === 0) || (row % 2 === 1 && col % 2 === 1)
  }
  // Calculate the position in the set of four boxes (0 to 3)
  const positionInGroup = idx % 4
  // The first and last in each group of four are clear
  return positionInGroup === 0 || positionInGroup === 3
}

const randBgPos = () => {
  return Math.floor(Math.random() * 99)
}

const newLineAnimation = () => {
  gsap.fromTo(
    `.${topLineV}`,
    { scaleY: 0 },
    {
      scaleY: 1,
      scrollTrigger: {
        trigger: section.value,
        id: topLineV,
        start: 'top+=50 center',
        end: 'top+=200 center',
        scrub: true,
        markers: false,
      },
    }
  )

  gsap.fromTo(
    `.${topLineH}`,
    { scaleX: 0 },
    {
      scaleX: 1,
      scrollTrigger: {
        trigger: section.value,
        id: topLineH,
        start: 'top-=100 center',
        end: 'top+=50 center',
        scrub: true,
        markers: false,
      },
    }
  )

  gsap.fromTo(
    `.${orb}`,
    { scale: 0, opacity: 0, y: -40 },
    {
      scale: 1,
      opacity: 1,
      y: device.value === 'mobile' ? 20 : 40,
      scrollTrigger: {
        trigger: section.value,
        id: `so-${orb}`,
        start: 'top+=100 center',
        end: 'top+=200 center',
        scrub: true,
        markers: false,
      },
    }
  )
}

onMounted(() => {
  newLineAnimation()
})

onBeforeUnmount(() => {
  ScrollTrigger.getById(topLineH)?.kill()
  ScrollTrigger.getById(topLineV)?.kill()
  ScrollTrigger.getById(orb)?.kill()
})
</script>

<style lang="scss">
.grid-module-component {
  @apply flex flex-col items-center;

  .inner-container.top {
    @apply relative pb-20 my-0 lg:mb-10;

    .horizontal-line-top {
      @apply h-[1px] w-6/12 origin-left;
    }

    .vertical-line-top {
      @apply absolute h-10 lg:h-20 w-[1px] left-1/2 top-0 z-10 origin-top;
    }

    .orb {
      @apply absolute w-6 lg:w-8 h-6 lg:h-8 rounded-full left-[calc(50%-12px)] lg:left-[calc(50%-16px)] blur-[2px] top-2;
    }
  }

  .inner-container {
    .wrapper {
      .sub-heading-area {
        @apply pt-10 lg:pt-4;
      }
    }
  }

  .title {
    @apply mb-8 flex flex-col justify-center items-center;

    &-inner {
      @apply heading-2 text-lm_purple text-center w-[80%] max-w-[1000px];
    }
  }

  .grid {
    @apply flex flex-wrap bg-[url(@/assets/img/benefits_background.jpg)] bg-cover max-w-[1920px];

    // background-image: ;
    .box {
      @apply text-white text-xl tracking-[0.2px] h-[128px] flex-2 px-[5%] flex items-center justify-center text-center lg:flex-4 lg:h-[222px] lg:text-[28px] lg:leading-8 lg:tracking-[0.28px];

      &.clear {
        @apply bg-[url(@/assets/img/benefits_foreground.jpg)] bg-cover bg-no-repeat;
        background-size: 400%;
      }
    }
  }
}
</style>
